import React, { useState } from "react";

const DeleteUserConfirmation = ({
  isOpen,
  onClose,
  user,
  hasDocuments,
  documentCount = 0,
  userList,
  onConfirm
}) => {
  const [action, setAction] = useState("close"); // 'delete' or 'close'
  const [transferDocuments, setTransferDocuments] = useState(false);
  const [targetUserId, setTargetUserId] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);

  const handleConfirm = async () => {
    // Validation
    if (action === "delete" && hasDocuments) {
      setError("Cannot delete user with documents. Use 'Close' instead.");
      return;
    }

    if (action === "close" && transferDocuments && !targetUserId) {
      setError("Please select a user to transfer documents to.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      await onConfirm({
        action,
        transferDocuments: action === "close" ? transferDocuments : false,
        targetUserId: transferDocuments ? targetUserId : null
      });
      handleClose();
    } catch (err) {
      setError(err.message || "Failed to delete/close user");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setAction("close");
    setTransferDocuments(false);
    setTargetUserId("");
    setError("");
    setIsConfirmed(false);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
      style={{ zIndex: 9999 }}
    >
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-xl">
        <h3 className="text-lg font-semibold mb-4 text-red-600">
          Delete or Close User
        </h3>

        <div className="bg-yellow-50 border border-yellow-200 rounded p-4 mb-4">
          <p className="text-sm text-yellow-800">
            <strong>Warning:</strong> This action requires confirmation. Please
            review the options carefully.
          </p>
        </div>

        <div className="mb-4">
          <p className="text-sm mb-2">
            <strong>User:</strong> {user?.Name} ({user?.Email})
          </p>
          {hasDocuments && (
            <p className="text-sm text-orange-600 mb-2">
              <strong>Documents:</strong> This user has {documentCount}{" "}
              document(s) assigned.
            </p>
          )}
        </div>

        {/* Action Selection */}
        <div className="space-y-3 mb-6">
          <h4 className="font-medium text-sm mb-2">Select Action:</h4>

          {/* Delete Option */}
          <label
            className={`flex items-start cursor-pointer p-4 border rounded ${
              hasDocuments
                ? "bg-gray-100 cursor-not-allowed opacity-60"
                : "hover:bg-base-200"
            }`}
          >
            <input
              type="radio"
              name="action"
              value="delete"
              checked={action === "delete"}
              onChange={(e) => {
                setAction(e.target.value);
                setTransferDocuments(false);
                setTargetUserId("");
              }}
              className="op-radio mt-1 mr-3"
              disabled={hasDocuments}
            />
            <div className="flex-1">
              <div className="font-medium text-sm text-red-600">
                Delete User Permanently
              </div>
              <div className="text-xs text-base-content/70 mt-1">
                Completely removes the user from the system. Only available if
                the user has no documents and no team members.
              </div>
              {hasDocuments && (
                <div className="text-xs text-red-500 mt-1">
                  ✗ Cannot delete: User has documents assigned
                </div>
              )}
            </div>
          </label>

          {/* Close Option */}
          <label className="flex items-start cursor-pointer p-4 border rounded hover:bg-base-200">
            <input
              type="radio"
              name="action"
              value="close"
              checked={action === "close"}
              onChange={(e) => {
                setAction(e.target.value);
              }}
              className="op-radio mt-1 mr-3"
            />
            <div className="flex-1">
              <div className="font-medium text-sm text-orange-600">
                Close User Account
              </div>
              <div className="text-xs text-base-content/70 mt-1">
                Marks the user as disabled. The user account will be retained
                and accessible only by system administrators. Documents can
                optionally be transferred to another user.
              </div>
            </div>
          </label>
        </div>

        {/* Document Transfer Options (only for Close action) */}
        {action === "close" && hasDocuments && (
          <div className="border-t pt-4 mb-4">
            <h4 className="font-medium text-sm mb-3">
              Document Ownership Options:
            </h4>

            <div className="space-y-3">
              {/* Option 1: Leave documents alone */}
              <label className="flex items-start cursor-pointer p-3 border rounded hover:bg-base-200">
                <input
                  type="radio"
                  name="transferOption"
                  checked={!transferDocuments}
                  onChange={() => {
                    setTransferDocuments(false);
                    setTargetUserId("");
                  }}
                  className="op-radio mt-1 mr-3"
                />
                <div className="flex-1">
                  <div className="font-medium text-sm">
                    Option 1: Keep Current Ownership
                  </div>
                  <div className="text-xs text-base-content/70 mt-1">
                    Documents remain assigned to this user. System
                    administrators can access them later.
                  </div>
                </div>
              </label>

              {/* Option 2: Transfer documents */}
              <label className="flex items-start cursor-pointer p-3 border rounded hover:bg-base-200">
                <input
                  type="radio"
                  name="transferOption"
                  checked={transferDocuments}
                  onChange={() => {
                    setTransferDocuments(true);
                  }}
                  className="op-radio mt-1 mr-3"
                />
                <div className="flex-1">
                  <div className="font-medium text-sm">
                    Option 2: Transfer Ownership
                  </div>
                  <div className="text-xs text-base-content/70 mt-1">
                    Transfer all documents to another user or admin.
                  </div>
                </div>
              </label>

              {transferDocuments && (
                <div className="ml-8">
                  <label className="block text-sm font-medium mb-2">
                    Transfer documents to:
                  </label>
                  <select
                    value={targetUserId}
                    onChange={(e) => setTargetUserId(e.target.value)}
                    className="op-select op-select-bordered w-full"
                    disabled={isLoading}
                  >
                    <option value="">Select user</option>
                    {userList
                      .filter((u) => u.objectId !== user?.objectId)
                      .map((u) => (
                        <option key={u.objectId} value={u.UserId?.objectId}>
                          {u.Name} ({u.Email}) -{" "}
                          {u.UserRole?.replace("contracts_", "")}
                        </option>
                      ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="alert alert-error mb-4">
            <span>{error}</span>
          </div>
        )}

        {/* Confirmation Checkbox */}
        <div className="bg-red-50 border border-red-200 rounded p-4 mb-4">
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              id="confirmDelete"
              checked={isConfirmed}
              onChange={(e) => setIsConfirmed(e.target.checked)}
              className="op-checkbox op-checkbox-error mt-1 mr-3"
              disabled={isLoading}
            />
            <label htmlFor="confirmDelete" className="text-sm text-red-700">
              I understand that{" "}
              {action === "delete"
                ? "this will permanently delete the user and cannot be undone"
                : "this will close the user account and they will no longer be able to access the system"}
              .
            </label>
          </label>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-2">
          <button
            onClick={handleClose}
            className="op-btn op-btn-secondary"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className={`op-btn ${
              action === "delete" ? "op-btn-error" : "op-btn-warning"
            }`}
            disabled={isLoading || !isConfirmed}
          >
            {isLoading
              ? "Processing..."
              : action === "delete"
                ? "Delete User"
                : "Close User"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteUserConfirmation;
